import { omit } from 'lodash-es'
import * as Yup from 'yup'

import { filterVisibleItems } from '../../../../../utils/common'
import { isFormFieldsEqual } from '../../../../../utils/formHelpers'
import { formValidationSchemaNoChanged } from '../../../../Form'

import {
  NEWSBOARD_POST_FORM_RECIPIENTS_CHECKBOX_SOME_REQUIRED_FIELDS,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_ADD_INFO_FIELDS,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_ALL_EMPLOYEES,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING_GROUP,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_NON_REGISTERED_USERS_CHECKBOX,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_RADIO_BUILDINGS,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_REGISTERED_USERS_CHECKBOX,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_STATUS,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_TV_SCREENS,
  NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_USER_INFO_STATUS,
  RecipientAudienceOptionValue,
} from './RecipientsField.constants'

function isEqual({ initialValues, values }) {
  const excludeFields = [
    NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_STATUS,
    NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_ADD_INFO_FIELDS,
  ]

  return isFormFieldsEqual({
    initialValues: omit(initialValues, excludeFields),
    values: omit(values, excludeFields),
  })
}

function checkIsOneOfValueExists(values) {
  const currentValues = this?.parent || values

  // one of checkbox should be true
  return NEWSBOARD_POST_FORM_RECIPIENTS_CHECKBOX_SOME_REQUIRED_FIELDS.some(
    item => currentValues[item]
  )
}

function isBuildingRadio(radio) {
  return radio === RecipientAudienceOptionValue.Building
}

function isBuildingGroupsRadio(radio) {
  return radio === RecipientAudienceOptionValue.BuildingGroups
}

function isBuildingBlockInactiveByAudience(tv, nonRegister, register) {
  return !(tv || nonRegister || register)
}

export function getBoardRadioOptions(isStaffUserAcc, t) {
  return filterVisibleItems([
    {
      id: RecipientAudienceOptionValue.Building,
      isVisible: true,
      label: t('Newsboard.RecipientAudienceForBuildingsValueOptionKey'),
    },
    {
      id: RecipientAudienceOptionValue.BuildingGroups,
      isVisible: isStaffUserAcc,
      label: t('Newsboard.RecipientAudienceForBuildingGroupsValueOptionKey'),
    },
  ])
}

export function getValidationSchema({ initialValues, isStaffUserAcc }) {
  if (!isStaffUserAcc) {
    const nonStaffSchema = Yup.object({
      [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING]: Yup.array()
        .min(1, true)
        .required(),
    })

    return Yup.lazy(values =>
      values[NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING].length > 0
        ? nonStaffSchema.concat(
            formValidationSchemaNoChanged({ initialValues, isEqual })
          )
        : nonStaffSchema
    )
  }

  const checkboxGroupValidation = Yup.boolean().test({
    message: true,
    name: 'at-least-one-checked',
    test: checkIsOneOfValueExists,
  })

  const base = Yup.object({
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_ALL_EMPLOYEES]:
      checkboxGroupValidation,
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING]: Yup.lazy(() => {
      return Yup.array().when(
        [
          ...NEWSBOARD_POST_FORM_RECIPIENTS_CHECKBOX_SOME_REQUIRED_FIELDS,
          NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING_GROUP,
          NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_RADIO_BUILDINGS,
        ],
        {
          is: (employees, tv, nonRegister, register, buildingGroup, radio) =>
            isBuildingGroupsRadio(radio) ||
            isBuildingBlockInactiveByAudience(tv, nonRegister, register),
          otherwise: () => Yup.array().min(1, true).required(),
          then: () => Yup.array().notRequired(),
        }
      )
    }),
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING_GROUP]: Yup.lazy(() => {
      return Yup.array().when(
        [
          ...NEWSBOARD_POST_FORM_RECIPIENTS_CHECKBOX_SOME_REQUIRED_FIELDS,
          NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_BUILDING,
          NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_RADIO_BUILDINGS,
        ],
        {
          is: (employees, tv, nonRegister, register, building, radio) =>
            isBuildingRadio(radio) ||
            isBuildingBlockInactiveByAudience(tv, nonRegister, register),
          otherwise: () => Yup.array().min(1, true).required(),
          then: () => Yup.array().notRequired(),
        }
      )
    }),
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_NON_REGISTERED_USERS_CHECKBOX]:
      checkboxGroupValidation,
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_REGISTERED_USERS_CHECKBOX]:
      checkboxGroupValidation,
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_TV_SCREENS]:
      checkboxGroupValidation,
    [NEWSBOARD_POST_FORM_RECIPIENTS_FIELD_NAME_USER_INFO_STATUS]: Yup.array(),
  })

  return Yup.lazy(values =>
    checkIsOneOfValueExists(values)
      ? base.concat(formValidationSchemaNoChanged({ initialValues, isEqual }))
      : base
  )
}
